import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useLiveMatch } from '../../hooks/useLiveMatch';
import LiveScoreboard from './LiveScoreboard';
import KillFeed from './KillFeed';
import RoundHistory from './RoundHistory';
import WinProbability from './WinProbability';
import MatchPrediction from '../matches/MatchPrediction';
import MatchComments from '../matches/MatchComments';
import FeatureBoundary from '../common/FeatureBoundary';
import './LiveMatchHub.css';

/**
 * LiveMatchHub — samlingsvyn för en pågående match (fas 3, match-hubben).
 *
 * Kombinerar det WebSocket-drivna live-statet (score, spelare, killfeed,
 * rundhistorik, win probability) med matchtips-kortet och kommentarerna.
 * Initialt state hämtas via REST (`/api/live/match/{id}`) så vyn är komplett
 * direkt vid sidladdning; WS-meddelanden tar sedan över uppdateringarna.
 *
 * @param {{summary: object}} props Rad från `/api/matches/live` (matchid,
 *   lagnamn, karta, GOTV) som fallback tills live-statet levererats.
 * @returns {JSX.Element} Hela hubben för en live-match.
 */
const LiveMatchHub = ({ summary }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { matchState } = useLiveMatch();
  const [restState, setRestState] = useState(null);

  const matchId = String(summary.matchid);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/live/match/${matchId}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data && data.match_id) setRestState(data);
      })
      .catch(() => {
        // tyst — WS-statet tar över så snart ett event kommer
      });
    return () => { cancelled = true; };
  }, [matchId]);

  // WS-statet vinner när det gäller just den här matchen, annars REST-snapshotten.
  const live = matchState && String(matchState.match_id) === matchId
    ? matchState
    : restState;

  const team1Name = live?.team1?.name || summary.team1_name || 'Team 1';
  const team2Name = live?.team2?.name || summary.team2_name || 'Team 2';
  const team1Score = live?.team1?.score ?? summary.team1_score ?? 0;
  const team2Score = live?.team2?.score ?? summary.team2_score ?? 0;
  const mapName = live?.map_name || summary.mapname || '';

  return (
    <div className="live-hub">
      <div className="live-hub-header">
        <div className="live-hub-badge">
          <span className="live-dot"></span>
          LIVE
        </div>
        {mapName && <span className="live-hub-map">{mapName}</span>}
        <button
          type="button"
          className="live-hub-details-link"
          onClick={() => navigate(`/stats/matches/${matchId}`)}
        >
          {t('live.hubMatchPage')} →
        </button>
      </div>

      <div className="live-hub-score">
        <span className="live-hub-team">{team1Name}</span>
        <span className="live-hub-score-numbers">{team1Score} – {team2Score}</span>
        <span className="live-hub-team">{team2Name}</span>
      </div>

      {live?.round_number > 0 && live?.phase !== 'finished' && (
        <div className="live-hub-round">
          {t('live.hubRound')} {live.round_number}
          {live.phase === 'halftime' && ` · ${t('live.hubHalftime')}`}
          {live.phase === 'paused' && ` · ${t('live.hubPaused')}`}
        </div>
      )}

      <WinProbability
        probability={live?.win_probability ?? null}
        history={live?.win_prob_history || []}
        team1Name={team1Name}
        team2Name={team2Name}
      />

      {/* Live-hubben visar matchen som spelas — poängekonomin får inte kunna
          släcka scoreboarden, killfeeden eller rundhistoriken. */}
      <FeatureBoundary name="MatchPrediction">
        <MatchPrediction matchId={matchId} compact />
      </FeatureBoundary>

      {live && (
        <>
          <RoundHistory rounds={live.round_history || []} />
          <div className="live-hub-scoreboards">
            <LiveScoreboard team={live.team1} teamName={team1Name} side={live.team1?.side} />
            <LiveScoreboard team={live.team2} teamName={team2Name} side={live.team2?.side} />
          </div>
          <div className="live-hub-feed">
            <div className="live-hub-feed-title">{t('live.hubKillfeed')}</div>
            <KillFeed kills={live.kill_feed || []} />
          </div>
        </>
      )}

      <div className="live-hub-comments">
        <MatchComments matchid={matchId} />
      </div>
    </div>
  );
};

export default LiveMatchHub;
